import React, { useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '../context/AuthContext';

export default function AuthCallback() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const hasProcessed = React.useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash.replace(/^#/, '');
        const params = new URLSearchParams(hash);
        const session_id = params.get('session_id');
        if (!session_id) { navigate('/', { replace: true }); return; }

        (async () => {
            try {
                const res = await axios.post(`${API}/auth/google/session`, { session_id }, { withCredentials: true });
                // CRITICAL: scrub the hash BEFORE setUser. setUser triggers
                // an AuthProvider re-render; AppRouter re-checks the hash on
                // every render and if the session_id is still there it mounts
                // a brand-new AuthCallback that tries to exchange the already-
                // consumed code → 401 → navigate back to / → login loop.
                //
                // Honour a pending-invite stored on the /invite/<code> page —
                // without this, anyone who signs in via the Architect invite
                // link lands on /app and the auto-claim useEffect never runs,
                // so the invite is silently dropped and the user stays on
                // Foundation tier (the bug reported on 2026-05-19).
                let destination = '/app';
                try {
                    const pendingInvite = sessionStorage.getItem('defectr:pending-invite');
                    if (pendingInvite) {
                        destination = `/invite/${encodeURIComponent(pendingInvite)}`;
                    }
                    // Also honour any generic `return_to` from the team-invite
                    // flow so that /team/accept?token=xxx survives the OAuth
                    // round-trip cleanly.
                    const returnTo = sessionStorage.getItem('defectr:return_to');
                    if (returnTo) {
                        sessionStorage.removeItem('defectr:return_to');
                        destination = returnTo;
                    }
                } catch { /* sessionStorage may be unavailable in private mode */ }
                window.history.replaceState({}, '', destination);
                setUser(res.data.user);
                navigate(destination, { replace: true, state: { user: res.data.user } });
            } catch {
                window.history.replaceState({}, '', '/');
                navigate('/', { replace: true });
            }
        })();
    }, [navigate, setUser]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-white">
            <div className="text-center">
                <img
                    src="/defectr-logo.png"
                    alt="DefectR"
                    className="h-24 w-auto mx-auto animate-pulse"
                />
                <p className="mt-4 label-tiny">Signing you in…</p>
            </div>
        </div>
    );
}
